import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnFeedQueryParameters, ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { FsNotifyChannel, FsNotifyMessage, FsNotifySubscriber, SnNotify } from '@lib/api/entities';
import { t } from '@lib/translation/helpers/translation.helper';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnNotifyApi {
  constructor(
    private readonly _http: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getMessageList(params?: ISnListQueryParameters): Observable<ISnListResponse<SnNotify>> {
    return this._http.get('/api/notify/messages/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        ordering: response.ordering,
        results: response.results.map((message) => new SnNotify(message)),
      })),
    );
  }

  public getChannelList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FsNotifyChannel>> {
    return this._http.get('/api/notify/channels/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        ordering: response.ordering,
        results: response.results.map((channel: FsNotifyChannel) => new FsNotifyChannel(channel)),
      })),
    );
  }

  public getChannelMessages(
    channelId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FsNotifyMessage>> {
    return this._http.get(`/api/notify/channels/${channelId}/messages/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        ordering: response.ordering,
        results: response.results.map((message: FsNotifyMessage) => new FsNotifyMessage(message)),
      })),
    );
  }

  public getChannelSubscribers(
    channelId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FsNotifySubscriber>> {
    return this._http.get(`/api/notify/channels/${channelId}/subscribers/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        ordering: response.ordering,
        results: response.results.map(
          (subscriber: FsNotifySubscriber) => new FsNotifySubscriber(subscriber),
        ),
      })),
    );
  }

  public getChannel(id: string): Observable<FsNotifyChannel> {
    return this._http
      .get(`/api/notify/channels/${id}/`)
      .pipe(map((response: any) => new FsNotifyChannel(response)));
  }

  public getZipcodeInChannel(id: string): Observable<any> {
    return this._http
      .get(`/api/notify/channels/${id}/zipcodes`)
      .pipe(map((response: any) => response.results.map((zipcode: any) => zipcode)));
  }

  public sendNotifyMessage(channelId: any, data: any): Observable<FsNotifyMessage> {
    return this._http.post(`/api/notify/channels/${channelId}/messages/`, data).pipe(
      map((response: SnNotify) => new FsNotifyMessage(response)),
      tap({
        next: () => this._toastrService.success(t('notify.messages.created')),
        error: () =>
          this._toastrService.error(
            t('notify.messages.createError'),
          ),
      }),
    );
  }

  public deleteMessage(channelId: string, messageId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('notify.messages.deleteConfirm'),
        confirmButton: {
          text: t('common.delete'),
          color: 'danger',
        },
      })
      .pipe(
        switchMap(() =>
          this._http.delete(`/api/notify/channels/${channelId}/messages/${messageId}/`),
        ),
        tap({
          next: () => this._toastrService.success(t('notify.messages.deleted')),
          error: () =>
            this._toastrService.error(
              t('notify.messages.deleteError'),
            ),
        }),
      );
  }

  public addSubscriber(channelId: any, data: any): Observable<FsNotifySubscriber> {
    return this._http.post(`/api/notify/channels/${channelId}/subscribers/`, data).pipe(
      map((response) => new FsNotifySubscriber(response)),
      tap({
        next: () => this._toastrService.success(t('notify.subscribers.added')),
        error: (err: HttpErrorResponse) => {
          if (err.error?.message?.length) {
            this._toastrService.error(err.error.message[0]);
          } else {
            this._toastrService.error(
              t('notify.subscribers.addError'),
            );
          }
        },
      }),
    );
  }

  public deleteSubscriber(channelId: string, subscriberId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('notify.subscribers.removeConfirm'),
        confirmButton: {
          text: t('common.delete'),
          color: 'danger',
        },
      })
      .pipe(
        switchMap(() =>
          this._http.delete(`/api/notify/channels/${channelId}/subscribers/${subscriberId}/`),
        ),
        tap({
          next: () => this._toastrService.success(t('notify.subscribers.removed')),
          error: () =>
            this._toastrService.error(
              t('notify.subscribers.removeError'),
            ),
        }),
      );
  }

  public deleteChannel(channelId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('notify.channels.deleteConfirm'),
        confirmButton: {
          text: t('common.delete'),
          color: 'danger',
        },
      })
      .pipe(
        switchMap(() => this._http.delete(`/api/notify/channels/${channelId}/`)),
        tap({
          next: () => this._toastrService.success(t('notify.channels.deleted')),
          error: () =>
            this._toastrService.error(
              t('notify.channels.deleteError'),
            ),
        }),
      );
  }

  public feedMessage(params?: ISnFeedQueryParameters): Observable<SnNotify[]> {
    return this._http.get('/api/notify/messages/feed/', { params }).pipe(
      map((response: any) => {
        return response.map((locations) => new SnNotify(locations));
      }),
    );
  }

  public feedChannels(params?: ISnFeedQueryParameters): Observable<FsNotifyChannel[]> {
    return this._http.get('/api/notify/channels/feed/', { params }).pipe(
      map((response: any) => {
        return response.map((channel) => new FsNotifyChannel(channel));
      }),
    );
  }
}
